import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE } from "../lib/protocols";
import { openDiagnostica } from "./DiagnosticaModal";
import { useSectionNavigation } from "../lib/navigation";

const H1_LINES = [
  [{ t: "TUTTI TI VENDONO CASE." }],
  [{ t: "NOI GESTIAMO IL TUO" }],
  [
    { t: "PERCORSO", gold: true },
    { t: " IMMOBILIARE." },
  ],
];

const H1_LINES_SMALL = [
  [{ t: "TUTTI TI" }],
  [{ t: "VENDONO CASE." }],
  [{ t: "NOI GESTIAMO" }],
  [{ t: "IL TUO" }],
  [{ t: "PERCORSO", gold: true }],
  [{ t: "IMMOBILIARE." }],
];

const STATS = [
  { value: "50+", label: "Cantieri seguiti dall'impresa partner in oltre 30 anni" },
  { value: "100+", label: "Immobili analizzati" },
  { value: "1", label: "Referente per tutto il percorso" },
];

export default function HeroKineticSection() {

  const goToSection = useSectionNavigation();
  const [isSmall] = useState(() => window.innerWidth < 768);
  const [linesDone, setLinesDone] = useState(false);
  const h1Lines = isSmall ? H1_LINES_SMALL : H1_LINES;
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section className="relative flex min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Logo ufficiale A.C. HUB — globo dorato dell'ecosistema"
          className="h-[115%] w-full object-cover"
          style={{ filter: "brightness(0.6) contrast(1.05)" }}
        />
      </motion.div>
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(90deg, #050505 6%, rgba(5,5,5,0.88) 38%, rgba(5,5,5,0.35) 74%, rgba(5,5,5,0.72) 100%)",
        }}
      />
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background: "radial-gradient(ellipse 60% 45% at 72% 38%, rgba(201,164,92,0.16), transparent 70%)",
        }}
      />

      <div className="container-hub hero-inner relative z-[2]">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="eyebrow" data-testid="hero-eyebrow">
            Referente unico · Portale Digitale Riservato · Consulenza immobiliare integrata
          </p>
        </motion.div>

        <h1
          className={`display mt-7 max-w-[1000px] leading-[1.06] ${
            isSmall ? "text-[clamp(28px,9vw,36px)]" : "text-[clamp(38px,5vw,72px)]"
          }`}
          data-testid="hero-title"
        >
          {h1Lines.map((line, i) => (
            <span key={i} className={`hero-line ${linesDone ? "hero-line-open" : ""}`}>
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.28 + i * (isSmall ? 0.09 : 0.14), ease: [0.16, 1, 0.3, 1] }}
                onAnimationComplete={i === h1Lines.length - 1 ? () => setLinesDone(true) : undefined}
                style={{ display: "block" }}
              >
                {line.map((part, j) => (
                  <span key={j} className={part.gold ? "gold-text" : ""}>{part.t}</span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="lead mt-8 max-w-[680px] italic text-[#dedbd4]"
          data-testid="hero-lead"
        >
          A.C. HUB è la regia unica che gestisce ogni fase del tuo percorso immobiliare.
          <br />
          Un unico referente di fiducia che unisce e coordina competenze, professionisti, tecnologia e un'impresa
          edile partner. Il tutto attraverso un sistema innovativo suddiviso in cinque protocolli, con strategie
          mirate per ogni specifico obiettivo. Partner e professionisti selezionati insieme a te e un portale
          digitale riservato per monitorare lo stato di avanzamento del tuo percorso.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.95 }}
          className="mt-10"
        >
          <p className="hero-manifesto" data-testid="hero-manifesto">
            «Lavoriamo per una sola parte, la tua. Tutelando esclusivamente i tuoi interessi.»
          </p>
          <p className="mt-5 text-[12px] uppercase tracking-[0.16em] text-[#929292]" data-testid="hero-cta-offer">
            Video-consulenza gratuita di 20 minuti • analisi precisa della tua situazione
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button data-testid="hero-primary-cta" onClick={() => openDiagnostica()} className="btn btn-gold">
              Prenota l'analisi strategica
              <ArrowUpRight size={15} />
            </button>
            <button data-testid="hero-secondary-cta" onClick={() => goToSection("protocolli")} className="btn btn-outline">
              Esplora i 5 protocolli
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 grid max-w-[680px] grid-cols-1 gap-px border border-[#c9a45c]/25 bg-[#c9a45c]/25 sm:grid-cols-3"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0b0b0b]/85 px-5 py-4 backdrop-blur-sm" data-testid="hero-stat">
              <div className="font-serif text-2xl text-gold-light">{s.value}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-[#929292]">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-8 z-[2] hidden flex-col items-center gap-3 md:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
      >
        <span className="text-[9px] uppercase tracking-[0.3em] text-[#929292] [writing-mode:vertical-lr]">
          Scorri
        </span>
        <ArrowDown size={14} className="animate-bounce text-gold" />
      </motion.div>
    </section>
  );
}
